import React, { useState, memo } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { FileUploadPreview } from '../../components/FileUploadPreview';
import { MissingOrInvalidFiles } from '../../components/MissingOrInvalidFiles';
import { Upload, Form, Input, Button, Typography, Card } from '../../../../../components';
import { validateExcel } from '../../utils/validation';
import { getMaxCompressedFileSizeMB } from '../../utils/uploadLimits';
import { getBytesfromMB } from '../../../../../lib/helpers';
import { REQUIRED_AWS_TRANSFORM_ANALYSIS_EXCEL_SHEETS } from '../../../../../constants/discovery';

const { Paragraph, Title, Text } = Typography;
const { Dragger } = Upload;

export const SelectAnalysisExcelFile = memo(({ resetForm, fileList, setFileList, handleDelete, completeStep, form }) => {
    const [validationError, setValidationError] = useState(null);
    const maxCompressedSize = getMaxCompressedFileSizeMB();

    const uploadProps = {
        name: 'awsTransformAnalysisUpload',
        multiple: false,
        accept: '.xlsx',
        beforeUpload: async (file) => {
            const maxSizeInBytes = getBytesfromMB(maxCompressedSize);
            if (file.size > maxSizeInBytes) {
                setValidationError({
                    message: (
                        <>
                            <Title level={2}>File size error</Title>
                            <Paragraph>File size exceeds the maximum allowed size of {maxCompressedSize} MB.</Paragraph>
                        </>
                    ),
                });
                return Upload.LIST_IGNORE;
            }
            const isValid = await validateExcel(
                file,
                REQUIRED_AWS_TRANSFORM_ANALYSIS_EXCEL_SHEETS,
                setValidationError,
                'awsTransformAnalysis',
            );
            if (!isValid) {
                return Upload.LIST_IGNORE;
            }
            return false;
        },
        fileList: fileList,
        onChange(info) {
            setFileList(info.fileList);
        },
    };

    return (
        <>
            {validationError ? (
                <MissingOrInvalidFiles error={validationError} resetForm={resetForm} />
            ) : (
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label={
                            <>
                                <Text>Import Name</Text>&nbsp;
                                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>(optional)</Text>
                            </>
                        }
                        name="importName"
                        extra="Automatically created if left blank"
                    >
                        <Input placeholder="Import Name" />
                    </Form.Item>
                    <Card className="upload-card">
                        <Form.Item
                            name="awsTransformAnalysisUpload"
                            rules={[{ required: true, message: 'Please upload an Excel file' }]}
                        >
                            {fileList.length === 0 ? (
                                <Dragger className="custom-upload" {...uploadProps}>
                                    <div className="ant-upload-drag-icon">
                                        <InboxOutlined className="upload-icon" />
                                    </div>
                                    <Text strong>Click or drag file to this area to upload</Text>
                                    <Paragraph className="text-light">
                                        Support for a single Excel file ({maxCompressedSize}MB max)
                                    </Paragraph>
                                </Dragger>
                            ) : (
                                <FileUploadPreview file={fileList[0]} handleDelete={handleDelete} />
                            )}
                        </Form.Item>
                    </Card>
                    <Form.Item className="button-group" style={{ marginBottom: 0 }}>
                        <div className="button-group">
                            <Button type="default" size="medium" disabled={fileList.length === 0} onClick={resetForm}>
                                Cancel
                            </Button>
                            <Button
                                onClick={completeStep}
                                type="primary"
                                htmlType="submit"
                                size="medium"
                                disabled={fileList.length === 0}
                            >
                                Continue
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            )}
        </>
    );
});
